import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../App";
import ClientLayout from "../../components/portal/ClientLayout";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Avatar, AvatarFallback } from "../../components/ui/avatar";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import {
  MessageSquare,
  Send,
  User,
  Wifi,
  WifiOff,
} from "lucide-react";

const ClientChat = () => {
  const { user, token, API } = useAuth();
  const { partnerId } = useParams();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // WebSocket URL (convert http to ws)
  const getWebSocketUrl = useCallback(() => {
    const wsProtocol = API.startsWith("https") ? "wss" : "ws";
    const wsHost = API.replace(/^https?:\/\//, "");
    return `${wsProtocol}://${wsHost}/chat/ws/${token}`;
  }, [API, token]);

  // Connect to WebSocket
  const connectWebSocket = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    const ws = new WebSocket(getWebSocketUrl());

    ws.onopen = () => {
      setIsConnected(true);
      // Clear any pending reconnect
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      
      if (data.type === "new_message") {
        // New message received from someone else
        const message = data.message;
        
        // Add to messages if we're chatting with this person
        if (message.sender_id === partnerId) {
          setMessages((prev) => [...prev, message]);
          // Send read receipt
          ws.send(JSON.stringify({ type: "read", sender_id: message.sender_id }));
        }
        
        // Update conversation list
        fetchConversations();
      } else if (data.type === "message_sent") {
        // Confirmation of our sent message (for other tabs)
        // Skip if we're on the same tab that sent it
      } else if (data.type === "typing") {
        // Someone is typing
        if (data.sender_id === partnerId) {
          setIsTyping(true);
          // Clear typing indicator after 3 seconds
          if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
          typingTimeoutRef.current = setTimeout(() => setIsTyping(false), 3000);
        }
      } else if (data.type === "messages_read") {
        // Our messages were read
        if (data.reader_id === partnerId) {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.sender_id === user?.user_id ? { ...msg, read: true } : msg
            )
          );
        }
      } else if (data.type === "pong") {
        // Heartbeat response
      }
    };

    ws.onclose = (event) => {
      setIsConnected(false);
      wsRef.current = null;
      
      // Attempt to reconnect after 3 seconds
      if (!reconnectTimeoutRef.current) {
        reconnectTimeoutRef.current = setTimeout(() => {
          connectWebSocket();
        }, 3000);
      }
    };

    ws.onerror = (error) => {
    };

    wsRef.current = ws;
  }, [getWebSocketUrl, partnerId, user?.user_id]);

  // Disconnect WebSocket on unmount
  useEffect(() => {
    connectWebSocket();

    // Heartbeat to keep connection alive
    const heartbeat = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: "ping" }));
      }
    }, 30000);

    return () => {
      clearInterval(heartbeat);
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [connectWebSocket]);

  useEffect(() => {
    fetchConversations();
  }, []);

  useEffect(() => {
    if (partnerId) {
      fetchMessages(partnerId);
      setIsTyping(false);
    }
  }, [partnerId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchConversations = async () => {
    try {
      const response = await fetch(`${API}/chat/conversations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setConversations(data);

      // If we have a partnerId, find and set the partner
      if (partnerId) {
        const partner = data.find((c) => c.partner.user_id === partnerId);
        if (partner) {
          setSelectedPartner(partner.partner);
        }
      }
    } catch (error) {
      console.error("Request failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (userId) => {
    try {
      const response = await fetch(`${API}/chat/messages/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setMessages(data);

      // Find and set the partner
      const conversation = conversations.find((c) => c.partner.user_id === userId);
      if (conversation) {
        setSelectedPartner(conversation.partner);
      }

      // Send read receipt via WebSocket
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: "read", sender_id: userId }));
      }
    } catch (error) {
      console.error("Request failed:", error);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !partnerId) return;

    setSending(true);
    try {
      const params = new URLSearchParams({
        receiver_id: partnerId,
        content: newMessage,
      });

      const response = await fetch(`${API}/chat/messages?${params.toString()}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Failed to send message");

      const data = await response.json();
      setMessages((prev) => [...prev, data]);
      setNewMessage("");
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  // Send typing indicator
  const handleTyping = () => {
    if (wsRef.current?.readyState === WebSocket.OPEN && partnerId) {
      wsRef.current.send(JSON.stringify({ type: "typing", receiver_id: partnerId }));
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const selectConversation = (partner) => {
    setSelectedPartner(partner);
    navigate(`/portal/chat/${partner.user_id}`);
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return "Today";
    } else if (date.toDateString() === yesterday.toDateString()) {
      return "Yesterday";
    } else {
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    }
  };

  return (
    <ClientLayout activeNav="chat">
      <div>
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="font-heading text-xl font-bold text-rnb-navy">
              Messages
            </h1>
            <p className="text-sm text-gray-400 mt-0.5">
              Chat with your consultant in real-time
            </p>
          </div>
          {/* Connection Status */}
          <Badge variant={isConnected ? "default" : "secondary"} className="gap-1">
            {isConnected ? (
              <>
                <Wifi className="h-3 w-3" />
                Live
              </>
            ) : (
              <>
                <WifiOff className="h-3 w-3" />
                Reconnecting...
              </>
            )}
          </Badge>
        </div>

            <div className="grid lg:grid-cols-3 gap-6 h-[calc(100vh-280px)]">
              {/* Conversations List */}
              <Card className="lg:col-span-1" data-testid="conversations-list">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Conversations</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <ScrollArea className="h-[calc(100vh-380px)]">
                    {loading ? (
                      <div className="flex justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
                      </div>
                    ) : conversations.length > 0 ? (
                      <div className="space-y-1 p-2">
                        {conversations.map((conv) => (
                          <button
                            key={conv.partner.user_id}
                            onClick={() => selectConversation(conv.partner)}
                            className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors text-left ${
                              partnerId === conv.partner.user_id
                                ? "bg-rnb-light"
                                : "hover:bg-slate-50"
                            }`}
                            data-testid={`conversation-${conv.partner.user_id}`}
                          >
                            <Avatar className="h-10 w-10">
                              <AvatarFallback className="bg-rnb-blue text-white">
                                {conv.partner.name?.[0] || "U"}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <p className="font-medium text-rnb-navy truncate">
                                  {conv.partner.name || "Unknown"}
                                </p>
                                {conv.unread_count > 0 && (
                                  <Badge className="ml-2 bg-rnb-teal">
                                    {conv.unread_count}
                                  </Badge>
                                )}
                              </div>
                              {conv.last_message && (
                                <p className="text-sm text-rnb-gray truncate">
                                  {conv.last_message.content}
                                </p>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-rnb-gray">
                        <MessageSquare className="h-12 w-12 mx-auto mb-4 text-rnb-gray/30" />
                        <p>No conversations yet</p>
                      </div>
                    )}
                  </ScrollArea>
                </CardContent>
              </Card>

              {/* Chat Area */}
              <Card className="lg:col-span-2 flex flex-col" data-testid="chat-area">
                {selectedPartner ? (
                  <>
                    {/* Chat Header */}
                    <CardHeader className="border-b pb-3">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-10 w-10">
                          <AvatarFallback className="bg-rnb-blue text-white">
                            {selectedPartner.name?.[0] || "U"}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <CardTitle className="text-lg">
                            {selectedPartner.name || "Unknown"}
                          </CardTitle>
                          <p className="text-sm text-rnb-gray">
                            {selectedPartner.role === "admin" || selectedPartner.role === "consultant"
                              ? "Your Consultant"
                              : selectedPartner.role}
                          </p>
                        </div>
                      </div>
                    </CardHeader>

                    {/* Messages */}
                    <CardContent className="flex-1 overflow-hidden p-0">
                      <ScrollArea className="h-[calc(100vh-520px)] p-4">
                        <div className="space-y-4">
                          {messages.map((msg, index) => {
                            const isOwn = msg.sender_id === user?.user_id;
                            const showDate =
                              index === 0 ||
                              formatDate(messages[index - 1]?.created_at) !==
                                formatDate(msg.created_at);

                            return (
                              <div key={msg.message_id}>
                                {showDate && (
                                  <div className="flex justify-center my-4">
                                    <span className="text-xs text-rnb-gray bg-slate-100 px-3 py-1 rounded-full">
                                      {formatDate(msg.created_at)}
                                    </span>
                                  </div>
                                )}
                                <div
                                  className={`flex ${isOwn ? "justify-end" : "justify-start"}`}
                                >
                                  <div
                                    className={`max-w-[70%] rounded-2xl px-4 py-2 ${
                                      isOwn
                                        ? "bg-rnb-blue text-white rounded-br-md"
                                        : "bg-slate-100 text-rnb-navy rounded-bl-md"
                                    }`}
                                    data-testid={`message-${msg.message_id}`}
                                  >
                                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                                    <div className={`flex items-center gap-1 mt-1 ${isOwn ? "justify-end" : ""}`}>
                                      <span className={`text-xs ${isOwn ? "text-white/70" : "text-rnb-gray"}`}>
                                        {formatTime(msg.created_at)}
                                      </span>
                                      {isOwn && msg.read && (
                                        <span className="text-xs text-white/70">✓✓</span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                          {isTyping && (
                            <div className="flex justify-start">
                              <div className="bg-slate-100 text-rnb-gray rounded-2xl rounded-bl-md px-4 py-2">
                                <span className="text-sm italic">typing...</span>
                              </div>
                            </div>
                          )}
                          <div ref={messagesEndRef} />
                        </div>
                      </ScrollArea>
                    </CardContent>

                    {/* Message Input */}
                    <div className="border-t p-4">
                      <form onSubmit={sendMessage} className="flex gap-2">
                        <Input
                          value={newMessage}
                          onChange={(e) => {
                            setNewMessage(e.target.value);
                            handleTyping();
                          }}
                          placeholder="Type a message..."
                          className="flex-1"
                          disabled={sending}
                          data-testid="message-input"
                        />
                        <Button type="submit" disabled={sending || !newMessage.trim()} data-testid="send-message-btn">
                          <Send className="h-4 w-4" />
                        </Button>
                      </form>
                    </div>
                  </>
                ) : (
                  <div className="flex-1 flex items-center justify-center text-center p-8">
                    <div>
                      <User className="h-16 w-16 text-rnb-gray/30 mx-auto mb-4" />
                      <h3 className="font-medium text-rnb-navy mb-2">
                        Select a conversation
                      </h3>
                      <p className="text-sm text-rnb-gray">
                        Choose a conversation from the list to start chatting
                      </p>
                    </div>
                  </div>
                )}
              </Card>
            </div>
          </div>
    </ClientLayout>
  );
};

export default ClientChat;
